import React, { useState } from "react";
import { toast } from "sonner";
import { PauseCircle, PlayCircle, Loader2, Lock } from "lucide-react";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const RULES = {
  monthly: ["Massimo 2 sospensioni negli ultimi 12 mesi.", "I giorni residui del mese vengono congelati.", "Potrai riattivare il piano solo dopo almeno 7 giorni.", "Alla riattivazione ritroverai tutti i giorni residui (nuova scadenza = riattivazione + giorni residui)."],
  annual: ["Massimo 1 sospensione negli ultimi 12 mesi.", "Potrai riattivare il piano solo dopo almeno 30 giorni.", "Alla riattivazione la scadenza annuale slitta in avanti esattamente dei mesi e giorni di pausa."],
};

export default function SuspensionCard({ status, onChanged }) {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const s = status.suspension || {};
  if (!s.eligible && !s.suspended) return null;
  const cycle = status.billing_cycle;

  const suspend = async () => {
    setBusy(true);
    try { await api.post("/billing/suspend"); toast.success("Abbonamento sospeso"); setConfirm(false); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sospensione non riuscita"); }
    setBusy(false);
  };
  const reactivate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/reactivate");
      toast.success(`Piano riattivato! Pausa di ${data.paused}: nuova scadenza ${fmtDate(data.new_expiry)}`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Riattivazione non riuscita"); }
    setBusy(false);
  };

  return (
    <div data-testid="suspension-card" className={`rounded-xl border p-5 shadow-sm mb-8 ${s.suspended ? "border-amber-300 bg-amber-50/60" : "border-slate-200/80 bg-card"}`}>
      {s.suspended ? (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <PauseCircle style={{ width: 22, height: 22 }} className="text-amber-600 mt-0.5" />
            <div>
              <p className="font-bold text-slate-900">Abbonamento in pausa dal {fmtDate(s.suspended_at)}</p>
              <p className="text-sm text-slate-600">
                {cycle === "monthly" ? `Giorni residui congelati: ${s.frozen_days_left}. ` : "La scadenza annuale slitterà della durata della pausa. "}
                {s.can_reactivate ? "Puoi riattivare il piano quando vuoi." : `Riattivazione disponibile dal ${fmtDate(s.unlock_date)}.`}
              </p>
            </div>
          </div>
          <Button data-testid="reactivate-btn" onClick={reactivate} disabled={!s.can_reactivate || busy} className="font-semibold">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : s.can_reactivate ? <PlayCircle style={{ width: 16, height: 16 }} className="mr-1.5" /> : <Lock style={{ width: 14, height: 14 }} className="mr-1.5" />}
            {s.can_reactivate ? "Riattiva" : `Riattiva (tra ${s.days_to_unlock} ${s.days_to_unlock === 1 ? "giorno" : "giorni"})`}
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="font-semibold text-slate-900">Sospendi Abbonamento</p>
            <p className="text-sm text-slate-500" data-testid="suspensions-left">
              Sospensioni disponibili: <strong>{s.suspensions_left} su {s.suspensions_max}</strong> negli ultimi 12 mesi
            </p>
          </div>
          <Button data-testid="suspend-btn" variant="outline" onClick={() => setConfirm(true)} disabled={!s.can_suspend} className="font-semibold">
            <PauseCircle style={{ width: 16, height: 16 }} className="mr-1.5" /> Sospendi Abbonamento
          </Button>
        </div>
      )}

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="suspend-confirm-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sospendere l'abbonamento?</DialogTitle>
            <DialogDescription>Regole del piano {cycle === "annual" ? "Pro Annuale" : "Pro Mensile"}:</DialogDescription>
          </DialogHeader>
          <ul className="text-sm text-slate-700 space-y-1.5 list-disc pl-5">
            {(RULES[cycle] || []).map((r) => <li key={r}>{r}</li>)}
            <li>Durante la pausa puoi consultare e scaricare i preventivi esistenti, ma non crearne di nuovi né usare le funzioni Pro.</li>
          </ul>
          <p className="text-xs text-slate-500">Ti restano {s.suspensions_left} sospensioni: dopo questa ne avrai {Math.max(0, s.suspensions_left - 1)}.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)}>Annulla</Button>
            <Button data-testid="suspend-confirm" onClick={suspend} disabled={busy} className="font-semibold bg-amber-600 hover:bg-amber-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Conferma sospensione
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
